import { useState, useId, useMemo } from "react";
import { PageNav } from "@/components/ui/page-nav";
import { SortableHeader } from "@/components/ui/sortable-header";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  ExpandedState,
  PaginationState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDownIcon,
  CornerDownRightIcon,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { CopyButton } from "@/components/ui/copy-button";
import type { CreatorAnalytics } from "./AnalyticsSummaryBar";
import { formatCurrency } from "@/lib/format";

interface CreatorLinkData {
  link_id: string;
  short_link: string;
  clicks: number;
  leads: number;
  sales: number;
  sale_amount: number;
}

interface GroupedCreatorAnalytics {
  username: string;
  avatar: string;
  clicks: number;
  leads: number;
  sales: number;
  sale_amount: number;
  links: CreatorLinkData[];
}

function groupByCreator(data: CreatorAnalytics[]): GroupedCreatorAnalytics[] {
  const grouped = new Map<string, GroupedCreatorAnalytics>();

  for (const item of data) {
    const existing = grouped.get(item.username);
    if (existing) {
      existing.clicks += item.clicks;
      existing.leads += item.leads;
      existing.sales += item.sales;
      existing.sale_amount += item.sale_amount;
      existing.links.push({
        link_id: item.link_id,
        short_link: item.short_link,
        clicks: item.clicks,
        leads: item.leads,
        sales: item.sales,
        sale_amount: item.sale_amount,
      });
    } else {
      grouped.set(item.username, {
        username: item.username,
        avatar: item.avatar,
        clicks: item.clicks,
        leads: item.leads,
        sales: item.sales,
        sale_amount: item.sale_amount,
        links: [
          {
            link_id: item.link_id,
            short_link: item.short_link,
            clicks: item.clicks,
            leads: item.leads,
            sales: item.sales,
            sale_amount: item.sale_amount,
          },
        ],
      });
    }
  }

  return Array.from(grouped.values());
}

interface AnalyticsTableProps {
  data: CreatorAnalytics[];
}

// sale_amount is in cents.
const formatSale = (cents: number) => formatCurrency(cents / 100);

const columns: ColumnDef<GroupedCreatorAnalytics>[] = [
  {
    id: "expand",
    header: "",
    cell: ({ row }) => {
      const hasMultipleLinks = row.original.links.length > 1;
      return hasMultipleLinks ? (
        <ChevronDownIcon
          className={`size-4 transition-transform opacity-60 ${row.getIsExpanded() ? "rotate-180" : ""
            }`}
        />
      ) : null;
    },
    size: 40,
    minSize: 40,
    maxSize: 40,
  },
  {
    accessorKey: "username",
    header: "Creator",
    cell: ({ row }) => {
      const { avatar, username, links } = row.original;
      return (
        <div className="flex items-center gap-2 min-w-32">
          <Avatar className="size-9">
            <AvatarImage src={avatar} alt={username} />
            <AvatarFallback className="text-xs">
              {username?.charAt(0)?.toUpperCase() || "?"}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start justify-center">
            <span className="font-medium">@{username}</span>
            {links && (
              <div className="flex items-center text-muted-foreground">
                <CornerDownRightIcon className="size-3.5 inline-block mr-1" />
                <a
                  href={links[0].short_link}
                  className="text-xs text-muted-foreground leading-tight hover:underline hover:text-foreground"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {links[0].short_link.replace("https://", "")}
                </a>
                {links.length > 1 && (
                  <span className="text-xs leading-tight ml-2">
                    +{links.length - 1}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "clicks",
    header: ({ column }) => <SortableHeader column={column}>Clicks</SortableHeader>,
    cell: ({ row }) => (
      <span className="font-mono">{row.getValue("clicks")}</span>
    ),
  },
  {
    accessorKey: "leads",
    header: ({ column }) => <SortableHeader column={column}>Leads</SortableHeader>,
    cell: ({ row }) => (
      <span className="font-mono">{row.getValue("leads")}</span>
    ),
  },
  {
    accessorKey: "sales",
    header: ({ column }) => <SortableHeader column={column}>Sales</SortableHeader>,
    cell: ({ row }) => (
      <span className="font-mono">{row.getValue("sales")}</span>
    ),
  },
  {
    accessorKey: "sale_amount",
    header: ({ column }) => <SortableHeader column={column}>Revenue</SortableHeader>,
    cell: ({ row }) => (
      <span className="font-mono">
        {formatSale(row.getValue("sale_amount"))}
      </span>
    ),
  },
];

export default function AnalyticsTable({ data }: AnalyticsTableProps) {
  const id = useId();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "sale_amount", desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const groupedData = useMemo(() => groupByCreator(data), [data]);

  const table = useReactTable({
    data: groupedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onExpandedChange: setExpanded,
    state: {
      sorting,
      pagination,
      expanded,
    },
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 border rounded-lg overflow-hidden shadow-xs">
        <Table className="table-fixed">
          <TableHeader className="sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-background">
                <TableHead className="w-10" />
                {headerGroup.headers.slice(1).map((header) => (
                  <TableHead
                    key={header.id}
                    className={header.id === "username" ? "w-80" : ""}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const hasMultipleLinks = row.original.links.length > 1;
                const isExpanded = row.getIsExpanded();
                return (
                  <>
                    <TableRow
                      key={row.id}
                      className={`${hasMultipleLinks ? "cursor-pointer" : ""} ${isExpanded ? "border-b-0" : ""
                        }`}
                      onClick={() => hasMultipleLinks && row.toggleExpanded()}
                    >
                      <TableCell className="w-10 px-3">
                        {hasMultipleLinks && (
                          <ChevronDownIcon
                            className={`size-4 transition-transform ${isExpanded ? "rotate-180" : ""
                              }`}
                          />
                        )}
                      </TableCell>
                      {row
                        .getVisibleCells()
                        .slice(1)
                        .map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                    </TableRow>
                    {isExpanded &&
                      row.original.links.map((link, index) => (
                        <TableRow
                          key={link.link_id}
                          className={`hover group ${index < row.original.links.length - 1
                              ? "border-b-0"
                              : ""
                            }`}
                        >
                          <TableCell className="w-10 px-3"></TableCell>
                          <TableCell className="h-full py-0">
                            <div className="flex items-center gap-1 pl-4 h-11">
                              <a
                                href={link.short_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground h-5 flex items-center justify-center hover:text-foreground hover:underline transition-colors truncate text-sm border-l border-muted-foreground/20 border-dashed ps-2 pl-3"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {link.short_link.replace("https://", "")}
                              </a>
                              <CopyButton
                                textToCopy={link.short_link}
                                buttonText=""
                                tooltipText="Copy link"
                                variant="ghost"
                                tooltipDelay={700}
                                className="size-7 [&_svg]:size-3.5 shrink-0 ml-auto hidden group-hover:flex transition-opacity hover:bg-muted-foreground/10 text-muted-foreground"
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-muted-foreground text-sm">
                              {link.clicks}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-muted-foreground text-sm">
                              {link.leads}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-muted-foreground text-sm">
                              {link.sales}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-muted-foreground text-sm">
                              {formatSale(link.sale_amount)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                  </>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="w-auto flex items-center justify-between gap-5 mt-2">
        <div className="flex items-center gap-3">
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger id={id} className="w-fit whitespace-nowrap h-7.5">
              <SelectValue placeholder="Select number of results" />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((pageSize) => (
                <SelectItem
                  key={pageSize}
                  value={pageSize.toString()}
                  className="h-7.5"
                >
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label htmlFor={id} className="max-sm:sr-only">
            Rows per page
          </Label>
        </div>

        <PageNav
          className="grow justify-end gap-3"
          page={table.getState().pagination.pageIndex + 1}
          pageCount={table.getPageCount()}
          total={table.getRowCount()}
          pageSize={table.getState().pagination.pageSize}
          onFirst={() => table.firstPage()}
          onPrevious={() => table.previousPage()}
          onNext={() => table.nextPage()}
          onLast={() => table.lastPage()}
        />
      </div>
    </div>
  );
}
